import { $prose } from '@milkdown/utils'
import {
  Plugin,
  PluginKey,
  Selection,
  TextSelection,
} from '@milkdown/prose/state'
import type { EditorState, Transaction } from '@milkdown/prose/state'
import { Fragment } from '@milkdown/prose/model'
import type {
  Node as ProseNode,
  NodeType,
  ResolvedPos,
  Schema,
} from '@milkdown/prose/model'
import type { EditorView } from '@milkdown/prose/view'
import { MDAST_JINJA_TOKEN, rangeHasCodeMark } from './jinjaToken'
import {
  createTokenRegex,
  isCompleteToken,
  isDelimiterShell,
} from './tokenRegex'

/**
 * `jinjaTokenNormalizer` — the plugin that keeps jinja token nodes well-formed as
 * the user edits, replacing the interim full-doc mark guard.
 *
 * A jinjaToken is an inline CONTENT node whose text IS the raw token. Editing it
 * like prose means the document can transiently hold malformed tokens (a broken
 * delimiter, an absorbed trailing char, a stray inline mark, an abandoned empty
 * shell). One `appendTransaction` runs a decision tree over the tokens touched by
 * each edit and repairs them; a `handleTextInput` prop prevents boundary
 * absorption up front; a `view()` hook re-runs the pass after IME composition.
 *
 * Two scans, deliberately different:
 *  - MARK STRIP is a full-doc walk on every docChanged (the retired
 *    `jinjaTokenMarkGuard`, folded in). It must be full-doc: a parent-gated
 *    `addMark` marks the token node via an `AddMarkStep`, whose `StepMap` is
 *    EMPTY, so step-map range derivation would never see it. Docs are short
 *    single duty-prompts, so the walk is cheap.
 *  - STRUCTURAL repair (delete / dissolve / absorption-split / tokenize) is
 *    incremental: step-map ranges on docChanged, selection ancestors on a pure
 *    caret move (a caret move has NO step maps, so the deferred cleanups would be
 *    dead code without this), and the caret's block on an IME retry.
 */

/**
 * Meta key a `compositionend` retry sets to re-run the pass after IME. Exported
 * so tests can drive the retry path without a real composition event.
 */
export const jinjaNormalizerRetryMeta = 'jinjaNormalizerRetry'
/**
 * Meta key carrying the settling pass count on the appended transaction. Exported
 * so tests can (a) assert the normalizer appended nothing, and (b) pre-set it at
 * the bail threshold to plant fixture text the normalizer must leave alone.
 */
export const jinjaNormalizerPassMeta = 'jinjaNormalizerPass'
/**
 * Meta key a blur handler sets to flush deferred cleanups when the editor loses
 * focus (e.g. clicking Save without moving the caret first, which would
 * otherwise leave an abandoned shell / malformed token to serialize on save).
 * Exported so tests can drive the flush without a real blur event.
 */
export const jinjaNormalizerFlushMeta = 'jinjaNormalizerFlush'
const RETRY_META = jinjaNormalizerRetryMeta
const PASS_META = jinjaNormalizerPassMeta
const FLUSH_META = jinjaNormalizerFlushMeta
const MAX_PASSES = 4
/** Object-replacement char: stands in for an inline node in the tokenize string. */
const PLACEHOLDER = '￼'

type Fix =
  | { kind: 'unmark'; from: number; to: number }
  | { kind: 'delete'; from: number; to: number }
  | { kind: 'dissolve'; from: number; to: number; text: string }
  | { kind: 'split'; from: number; to: number; frag: Fragment }
  | { kind: 'wrap'; from: number; to: number; value: string }

/** Position of the jinjaToken enclosing `$pos`, or null. O(depth). */
function enclosingTokenPos($pos: ResolvedPos): number | null {
  for (let d = $pos.depth; d > 0; d--) {
    if ($pos.node(d).type.name === MDAST_JINJA_TOKEN) return $pos.before(d)
  }
  return null
}

/**
 * The document position of the jinjaToken the collapsed caret sits inside, or
 * null (no caret, or caret not inside a token). O(depth) — never walks the doc.
 */
function caretTokenPos(state: EditorState): number | null {
  const sel = state.selection
  if (!sel.empty) return null
  return enclosingTokenPos(sel.$head)
}

/** The [start, end) content range of the textblock enclosing `$pos`, or null. */
function enclosingBlockRange(
  $pos: ResolvedPos
): { from: number; to: number } | null {
  for (let d = $pos.depth; d >= 0; d--) {
    if ($pos.node(d).isTextblock) {
      return { from: $pos.start(d), to: $pos.end(d) }
    }
  }
  return null
}

/**
 * Union of the ranges changed by ALL transactions (Codex #7), in `newState`
 * coordinates. Each transaction's step-map ranges are mapped forward through the
 * remaining maps of that transaction and then through every later transaction.
 * Exported for a focused unit test of the cross-transaction mapping.
 */
export function changedRanges(
  transactions: readonly Transaction[]
): Array<{ from: number; to: number }> {
  const ranges: Array<{ from: number; to: number }> = []
  transactions.forEach((tr, i) => {
    const maps = tr.mapping.maps
    maps.forEach((map, j) => {
      map.forEach((_oldStart, _oldEnd, newStart, newEnd) => {
        const rest = tr.mapping.slice(j + 1)
        let from = rest.map(newStart, -1)
        let to = rest.map(newEnd, 1)
        for (let k = i + 1; k < transactions.length; k++) {
          const later = transactions[k]
          if (!later) continue
          from = later.mapping.map(from, -1)
          to = later.mapping.map(to, 1)
        }
        ranges.push({ from, to })
      })
    })
  })
  return ranges
}

/** Split raw text into alternating plain / complete-token segments. */
function tokenizeText(text: string): Array<{ token: boolean; value: string }> {
  const re = createTokenRegex()
  const out: Array<{ token: boolean; value: string }> = []
  let last = 0
  let m: RegExpExecArray | null
  while ((m = re.exec(text)) !== null) {
    if (m[0].length === 0) {
      re.lastIndex++
      continue
    }
    if (m.index > last)
      out.push({ token: false, value: text.slice(last, m.index) })
    out.push({ token: true, value: m[0] })
    last = m.index + m[0].length
  }
  if (last < text.length) out.push({ token: false, value: text.slice(last) })
  return out
}

/** Build an inline fragment [text?, token, text?, ...] from segments. */
function buildFragment(
  segs: Array<{ token: boolean; value: string }>,
  tokenType: NodeType,
  schema: Schema
): Fragment {
  const nodes: ProseNode[] = []
  for (const s of segs) {
    if (!s.value) continue
    nodes.push(
      s.token
        ? tokenType.create(null, schema.text(s.value))
        : schema.text(s.value)
    )
  }
  return Fragment.fromArray(nodes)
}

/**
 * The inline text of a textblock as a flat string, plus a segment table mapping
 * string offsets back to document positions. An inline CONTENT-node jinjaToken
 * (or any inline leaf) becomes a SINGLE placeholder char but occupies its full
 * `nodeSize` (textContent + 2 boundary positions) in the doc — so a plain token
 * typed AFTER an existing one still maps to the right position (Codex #6).
 */
function inlineString(
  block: ProseNode,
  contentStart: number
): {
  str: string
  segs: Array<{
    strStart: number
    strEnd: number
    docStart: number
    docSize: number
    text: boolean
  }>
} {
  let str = ''
  const segs: Array<{
    strStart: number
    strEnd: number
    docStart: number
    docSize: number
    text: boolean
  }> = []
  let docPos = contentStart
  block.forEach((child) => {
    if (child.isText && child.type.name !== MDAST_JINJA_TOKEN) {
      const t = child.text ?? ''
      segs.push({
        strStart: str.length,
        strEnd: str.length + t.length,
        docStart: docPos,
        docSize: t.length,
        text: true,
      })
      str += t
    } else {
      segs.push({
        strStart: str.length,
        strEnd: str.length + 1,
        docStart: docPos,
        docSize: child.nodeSize,
        text: false,
      })
      str += PLACEHOLDER
    }
    docPos += child.nodeSize
  })
  return { str, segs }
}

function strToDoc(
  segs: ReturnType<typeof inlineString>['segs'],
  off: number
): number {
  for (const s of segs) {
    if (off >= s.strStart && off <= s.strEnd) {
      if (s.text) return s.docStart + (off - s.strStart)
      return off === s.strStart ? s.docStart : s.docStart + s.docSize
    }
  }
  return -1
}

function overlapsPlaceholder(
  segs: ReturnType<typeof inlineString>['segs'],
  s: number,
  e: number
): boolean {
  return segs.some((seg) => !seg.text && seg.strStart < e && seg.strEnd > s)
}

/**
 * Wrap plain-text complete tokens that (a) are entirely plain text, (b) intersect
 * a raw changed range (so a pre-existing token OUTSIDE the edit is untouched, and
 * an undone literal is not re-wrapped), (c) aren't under a code mark. Handles the
 * paste / programmatic path the input rule (live typing) doesn't.
 */
function tokenizePass(
  state: EditorState,
  tokenType: NodeType,
  schema: Schema,
  ranges: Array<{ from: number; to: number }>,
  fixes: Fix[]
): void {
  const blocks = new Set<number>()
  for (const r of ranges) {
    state.doc.nodesBetween(
      Math.max(0, r.from),
      Math.min(state.doc.content.size, r.to),
      (node, pos) => {
        if (node.isTextblock) blocks.add(pos)
        return true
      }
    )
  }
  for (const blockPos of blocks) {
    const block = state.doc.nodeAt(blockPos)
    if (!block) continue
    const { str, segs } = inlineString(block, blockPos + 1)
    const re = createTokenRegex()
    let m: RegExpExecArray | null
    while ((m = re.exec(str)) !== null) {
      if (m[0].length === 0) {
        re.lastIndex++
        continue
      }
      const s = m.index
      const e = m.index + m[0].length
      if (overlapsPlaceholder(segs, s, e)) continue
      const from = strToDoc(segs, s)
      const to = strToDoc(segs, e)
      if (from < 0 || to < 0) continue
      if (!ranges.some((r) => from < r.to && to > r.from)) continue
      if (rangeHasCodeMark(state, from, to)) continue
      fixes.push({ kind: 'wrap', from, to, value: m[0] })
    }
  }
}

export const jinjaNormalizerKey = new PluginKey('jinjaNormalizer')

/**
 * The normalizer plugin. Kept in a factory so the `editorView` reference (for
 * `view.composing`) and the `pendingRetry` flag live in one closure per editor.
 */
export const jinjaTokenNormalizer = $prose(() => {
  let editorView: EditorView | null = null
  let pendingRetry = false

  return new Plugin({
    key: jinjaNormalizerKey,

    props: {
      /**
       * Absorption prevention (case 3, up front): when the caret sits AT a
       * token boundary (start or end of the token's content), typing would
       * extend the content node. Insert the char just OUTSIDE the node instead
       * so it stays prose beside the pill, and return true. Middle-of-token
       * typing (real content edits) falls through to PM.
       */
      handleTextInput(view, from, to, text) {
        if (from !== to) return false
        const $from = view.state.doc.resolve(from)
        if ($from.parent.type.name !== MDAST_JINJA_TOKEN) return false
        const atStart = $from.parentOffset === 0
        const atEnd = $from.parentOffset === $from.parent.content.size
        if (!atStart && !atEnd) return false
        const tokenPos = $from.before($from.depth)
        const insertAt = atEnd ? tokenPos + $from.parent.nodeSize : tokenPos
        const tr = view.state.tr.insertText(text, insertAt)
        tr.setSelection(TextSelection.create(tr.doc, insertAt + text.length))
        view.dispatch(tr)
        return true
      },
    },

    view(view) {
      editorView = view
      // Mirror inputRules' own compositionend: defer past PM's end-of-
      // composition so `view.composing` has cleared, then re-run the pass.
      const onCompositionEnd = () => {
        if (!pendingRetry) return
        setTimeout(() => {
          if (!editorView || editorView.composing) return
          pendingRetry = false
          editorView.dispatch(editorView.state.tr.setMeta(RETRY_META, true))
        })
      }
      // Editor lost focus (e.g. clicked Save) — flush deferred cleanups so an
      // abandoned shell / malformed token doesn't serialize into the save. Defer
      // past the blur: if focus returns (a toolbar button that refocuses the
      // editor, per the empty-insert flow), it wasn't a real blur — skip.
      const onBlur = () => {
        setTimeout(() => {
          if (!editorView || editorView.hasFocus() || editorView.composing)
            return
          editorView.dispatch(editorView.state.tr.setMeta(FLUSH_META, true))
        })
      }
      view.dom.addEventListener('compositionend', onCompositionEnd)
      view.dom.addEventListener('blur', onBlur)
      return {
        destroy() {
          view.dom.removeEventListener('compositionend', onCompositionEnd)
          view.dom.removeEventListener('blur', onBlur)
          if (editorView === view) editorView = null
        },
      }
    },

    appendTransaction(transactions, oldState, newState) {
      // IME: never rewrite mid-composition; flag a retry for compositionend.
      if (editorView?.composing) {
        pendingRetry = true
        return null
      }
      // Undo/redo restore a known-good doc — never re-normalize (this preserves
      // the input rule's undo-restores-literal escape hatch).
      // Note: 'history$' is prosemirror-history's PluginKey.key, which is
      // construction-order-dependent. Holds today (one history plugin); import
      // the key directly if that ever changes — it isn't cleanly re-exported.
      if (transactions.some((tr) => tr.getMeta('history$'))) return null

      // Settling guard: the appended tr re-runs this hook; bail on runaway.
      const pass = transactions.reduce(
        (max, tr) => Math.max(max, (tr.getMeta(PASS_META) as number) ?? 0),
        0
      )
      if (pass >= MAX_PASSES) {
        console.warn('jinjaNormalizer: bailed after too many passes')
        return null
      }

      const schema = newState.schema
      const tokenType = schema.nodes[MDAST_JINJA_TOKEN]
      if (!tokenType) return null

      const isRetry = transactions.some((tr) => tr.getMeta(RETRY_META))
      const isFlush = transactions.some((tr) => tr.getMeta(FLUSH_META))
      const docChanged = transactions.some((tr) => tr.docChanged)

      const fixes: Fix[] = []
      // On a focus-loss flush, treat the caret as GONE so consider() stops
      // deferring a shell / broken token just because the caret is nominally
      // still inside it.
      const caretPos = isFlush ? null : caretTokenPos(newState)

      // --- MARK STRIP (full-doc) --- also on the IME retry: a mark applied to
      // a token DURING composition is skipped (that tr bails), and the retry
      // pass is `isRetry`, not docChanged, so gate on both.
      if (docChanged || isRetry) {
        newState.doc.descendants((node, pos) => {
          if (node.type.name === MDAST_JINJA_TOKEN && node.marks.length > 0) {
            fixes.push({ kind: 'unmark', from: pos, to: pos + node.nodeSize })
          }
        })
      }

      // --- STRUCTURAL decision tree, per candidate token ---
      const seen = new Set<number>()
      const consider = (node: ProseNode, pos: number): void => {
        if (node.type.name !== MDAST_JINJA_TOKEN || seen.has(pos)) return
        seen.add(pos)
        const text = node.textContent
        const inside = caretPos === pos
        const end = pos + node.nodeSize
        // (1) empty content → delete now
        if (text === '') {
          fixes.push({ kind: 'delete', from: pos, to: end })
          return
        }
        // (2) exactly one complete token → OK (marks handled by the full-doc strip)
        if (isCompleteToken(text)) return
        // (3) contains ≥1 complete token → absorption split, even caret-inside
        const segs = tokenizeText(text)
        if (segs.some((s) => s.token)) {
          fixes.push({
            kind: 'split',
            from: pos,
            to: end,
            frag: buildFragment(segs, tokenType, schema),
          })
          return
        }
        // (1b/4) no complete token inside
        if (inside) return // defer while the caret is still editing
        if (isDelimiterShell(text))
          fixes.push({ kind: 'delete', from: pos, to: end })
        else fixes.push({ kind: 'dissolve', from: pos, to: end, text })
      }

      if (docChanged && !isRetry) {
        const ranges = changedRanges(transactions)
        for (const r of ranges) {
          newState.doc.nodesBetween(
            Math.max(0, r.from - 1),
            Math.min(newState.doc.content.size, r.to + 1),
            (node, pos) => {
              consider(node, pos)
              return true
            }
          )
        }
        // Tokenize pass — skip right after any input rule fired: that next
        // transaction is `undoInputRule` (or an edit that cleared the pending
        // undoable), and its restored literal must NOT be re-wrapped (Codex #3).
        // Known ceiling: over-fires for ANY input rule (emDash etc.), but only ever
        // SKIPS one tokenize — never re-tokenizes an undo — and reload re-wraps.
        // Tighten to the jinja rule's own pending state only if it ever matters.
        const afterInputRule = oldState.plugins.some(
          (p) =>
            (p.spec as { isInputRules?: boolean }).isInputRules &&
            p.getState(oldState) != null
        )
        if (!afterInputRule) {
          tokenizePass(newState, tokenType, schema, ranges, fixes)
        }
      } else if (isRetry) {
        // IME retry: repair existing malformed token NODES in the caret's block
        // (absorption splits, broken/shell tokens) but do NOT run the tokenize
        // pass — re-tokenizing plain text here would re-wrap a literal the user
        // restored via undo (the undo escape hatch). `consider` only touches
        // existing jinjaToken nodes, so it can't re-wrap a plain undone literal;
        // a token completed purely via IME re-pills on the next edit instead.
        pendingRetry = false
        const block = enclosingBlockRange(newState.selection.$head)
        if (block) {
          newState.doc.nodesBetween(block.from, block.to, (node, pos) => {
            consider(node, pos)
            return true
          })
        }
      } else if (isFlush) {
        // Editor lost focus: flush deferred cleanups now. caretPos is null above,
        // so consider() no longer defers on "caret inside". A doc-wide scan is
        // fine — blur is rare and these docs are short.
        newState.doc.descendants((node, pos) => {
          consider(node, pos)
          return true
        })
      } else {
        // Selection-only move: run the tree on the token(s) the OLD selection
        // left (deferred empty-delete / dissolve). Resolve the enclosing token
        // of BOTH endpoints regardless of empty — a RANGE selection has no empty
        // caret, so caretTokenPos returns null and the cleanup would never fire
        // when the caret leaves by collapsing a range outside the token.
        // Positions are unchanged (selection-only trs don't edit the doc), so
        // the old positions are valid in newState. Only clean up once the new
        // selection no longer touches the token at all — a range reaching INTO
        // the token (e.g. a toolbar toggle) still counts as engaged.
        // Known ceiling: a docChanged tr co-dispatched with the selection move would
        // route to the docChanged branch instead of here — not reachable in the
        // current Crepe config; add a combined path only if that changes.
        const leftPositions = new Set<number>()
        for (const $p of [
          oldState.selection.$head,
          oldState.selection.$anchor,
        ]) {
          const p = enclosingTokenPos($p)
          if (p != null) leftPositions.add(p)
        }
        // Also any token the old selection SPANNED with both endpoints outside
        // it — enclosingTokenPos on the endpoints alone would miss it.
        oldState.doc.nodesBetween(
          oldState.selection.from,
          oldState.selection.to,
          (node, pos) => {
            if (node.type.name === MDAST_JINJA_TOKEN) leftPositions.add(pos)
          }
        )
        const sel = newState.selection
        for (const leftPos of leftPositions) {
          const node = newState.doc.nodeAt(leftPos)
          if (node?.type.name !== MDAST_JINJA_TOKEN) continue
          const engaged = sel.from < leftPos + node.nodeSize && sel.to > leftPos
          if (!engaged) consider(node, leftPos)
        }
      }

      if (fixes.length === 0) return null

      const tr = newState.tr
      const headBefore = newState.selection.head
      let caret: { idx: number; pos: number } | null = null
      // Descending so each fix's positions stay valid as later (lower) fixes apply.
      fixes.sort((a, b) => b.from - a.from)
      for (const fix of fixes) {
        switch (fix.kind) {
          case 'unmark':
            tr.removeMark(fix.from, fix.to)
            break
          case 'delete':
            tr.delete(fix.from, fix.to)
            break
          case 'dissolve':
            tr.replaceWith(fix.from, fix.to, schema.text(fix.text))
            break
          case 'wrap':
            tr.replaceWith(
              fix.from,
              fix.to,
              tokenType.create(null, schema.text(fix.value))
            )
            break
          case 'split': {
            tr.replaceWith(fix.from, fix.to, fix.frag)
            // Only the split that CONTAINED the original caret may move it —
            // otherwise a pass with MULTIPLE splits lets the lowest-position
            // one hijack the caret to the wrong pill.
            if (headBefore >= fix.from && headBefore <= fix.to) {
              // Land the caret OUTSIDE the rebuilt pills: after leading text
              // (`y{{ x }}`), at the end for a trailing text (`{{ x }}y`), else
              // between adjacent pills — after the first token (`{{a}}{{b}}`).
              const { firstChild, lastChild } = fix.frag
              const offset = firstChild?.isText
                ? firstChild.nodeSize
                : lastChild?.isText
                  ? fix.frag.size
                  : (firstChild?.nodeSize ?? fix.frag.size)
              caret = { idx: tr.steps.length, pos: fix.from + offset }
            }
            break
          }
        }
      }

      if (caret) {
        const mapped = tr.mapping.slice(caret.idx).map(caret.pos)
        tr.setSelection(Selection.near(tr.doc.resolve(mapped), 1))
      }
      tr.setMeta(PASS_META, pass + 1)
      return tr
    },
  })
})
